/**
 * Client-side validation for the lead form (09-landing-page.md §3.1) — mirrors, but does not
 * replace, the server's `PublicLeadDto` (apps/api `src/modules/sales/dto/public-lead.dto.ts`).
 * Blocking rules here are a superset of nothing the server wouldn't also reject; the server always
 * re-validates (never trust client-only checks for a public, unauthenticated endpoint).
 */

export interface LeadFormValues {
  fullName: string;
  phone: string;
  email: string;
  gradeInterestedId: string;
  schoolInterestedId: string;
  message: string;
  consent: boolean;
}

export type LeadFormFieldName = keyof LeadFormValues;

/** Field validation/focus order — also the order client-side errors are checked in on submit. */
export const FIELD_ORDER: LeadFormFieldName[] = [
  "fullName",
  "phone",
  "email",
  "gradeInterestedId",
  "schoolInterestedId",
  "message",
  "consent",
];

/** Egyptian mobile prefixes 010/011/012/015 + 8 digits (11 digits total) — advisory only (§3.1
 * "phone-format mismatch shown as amber... submit still allowed"), never blocks submission. */
const EGYPT_MOBILE_RE = /^01[0125]\d{8}$/;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const MESSAGE_MAX_LENGTH = 500;

export function isPhoneFormatLikelyValid(phone: string): boolean {
  return EGYPT_MOBILE_RE.test(phone.trim());
}

/** Returns an i18n key (under `leadForm.errors.*`) for a blocking error, or `null` if valid. Does
 * not check the honeypot/captcha — those aren't user-facing fields. */
export function validateField(
  name: LeadFormFieldName,
  values: LeadFormValues,
  gradeOptionsAvailable: boolean,
): string | null {
  switch (name) {
    case "fullName": {
      const trimmed = values.fullName.trim();
      if (!trimmed) return "leadForm.errors.fullNameRequired";
      if (trimmed.length < 3) return "leadForm.errors.fullNameTooShort";
      if (trimmed.length > 150) return "leadForm.errors.fullNameTooLong";
      return null;
    }
    case "phone": {
      const trimmed = values.phone.trim();
      if (!trimmed) return "leadForm.errors.phoneRequired";
      return null;
    }
    case "email": {
      const trimmed = values.email.trim();
      if (!trimmed) return null;
      if (!EMAIL_RE.test(trimmed)) return "leadForm.errors.emailInvalid";
      return null;
    }
    case "gradeInterestedId": {
      if (!gradeOptionsAvailable) return null; // Surfaced via the load-error/empty state instead.
      if (!values.gradeInterestedId) return "leadForm.errors.gradeRequired";
      return null;
    }
    case "schoolInterestedId":
      return null;
    case "message": {
      if (values.message.length > MESSAGE_MAX_LENGTH)
        return "leadForm.errors.messageTooLong";
      return null;
    }
    case "consent":
      return values.consent ? null : "leadForm.errors.consentRequired";
    default:
      return null;
  }
}

export function validateAll(
  values: LeadFormValues,
  gradeOptionsAvailable: boolean,
): {
  errors: Partial<Record<LeadFormFieldName, string>>;
  firstInvalid: LeadFormFieldName | null;
} {
  const errors: Partial<Record<LeadFormFieldName, string>> = {};
  let firstInvalid: LeadFormFieldName | null = null;
  for (const field of FIELD_ORDER) {
    const error = validateField(field, values, gradeOptionsAvailable);
    if (error) {
      errors[field] = error;
      firstInvalid ??= field;
    }
  }
  return { errors, firstInvalid };
}
